/**
 * エリア選択集計 (`AreaReportModal`) 専用の小型プレビュー地図 (2026-07-16、PDFエクスポート機能)。
 *
 * 選択した多角形 (エリア) と地点を静的に描画するだけの単純な地図で、`StreetSurveyResultsMap.tsx`
 * とは別実装 (投げ縄/線描画/クラスタ等のインタラクションは一切不要なため)。
 *
 * 地点は結果地図と同じ「ドーナツ表示」(`buildDonutSvgMarkup`) の DOM `Marker` で描画し、
 * ドーナツ中央に「解析対象となった地点一覧」と対応する番号を重ねる (2026-07-16 追加)。
 * エリア (多角形) はネイティブ MapLibre レイヤー (GeoJSON fill) のまま。
 *
 * 地点が近接していると画面上でドーナツ同士が重なり番号が読めなくなるため、投影後の画面座標が
 * 近い地点は「クラスタ」としてまとめ、クラスタの中心を基準に円状に少しずらして配置し、
 * 元の位置まで細い線 (吹き出し線) を引く「スパイダーファイ」処理を行う (2026-07-16 追加)。
 *
 * 縮尺バー (2026-07-16 追加) は MapLibre 標準の `ScaleControl` を使わず自前実装する。
 * `ScaleControl` は DOM オーバーレイのため `map.getCanvas()` に含まれず (`StreetSurveyResultsMap.tsx`
 * では問題ないが、本コンポーネントは `capture()` で地図を静止画化する PDF 用途があるため)、
 * オンスクリーン表示とキャプチャ画像の両方で同じ見た目にするには自前で計算・描画する必要がある。
 * 距離計算・「きりのいい数値」への丸めは MapLibre/Mapbox GL JS 本家 ScaleControl と同じアルゴリズム
 * (2点間の測地線距離 → 1/2/3/5/10系列に丸め) を踏襲する。
 *
 * DOM Marker は `map.getCanvas()` に含まれないため、`capture()` は
 * `apps/user/src/features/insights/IdeaWorkspaceMiniMap.tsx` と同じ「地図 canvas + 2D canvas
 * 合成」方式を踏襲する。ドーナツは SVG (`buildDonutSvgMarkup`) をそのまま `Image` として
 * 読み込んで `drawImage` するため、ドーナツの描画ロジックを canvas 用に再実装する必要はない
 * (オンスクリーン DOM 表示とキャプチャ画像で完全に同じ見た目になる)。キャプチャ時も
 * スパイダーファイ後の表示位置・縮尺バーとも同じ値を使うため、オンスクリーンと同じレイアウトになる。
 */
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl, { type Map as MaplibreMap } from 'maplibre-gl';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { buildDonutSvgMarkup, type DonutSegment } from './streetSurveyClusterDonut';

const POLYGON_SOURCE_ID = 'area-report-polygon';
const POLYGON_LAYER_ID = 'area-report-polygon-fill';
const LEADER_SOURCE_ID = 'area-report-leader-lines';
const LEADER_LAYER_ID = 'area-report-leader-lines-stroke';
const MARKER_SIZE = 32;
/** これより画面上で近い (px) 地点は重なるとみなしクラスタ化する。 */
const CLUSTER_THRESHOLD_PX = MARKER_SIZE + 8;
/** 縮尺バーの最大幅 (px)。この幅に収まる「きりのいい距離」を探す。 */
const SCALE_BAR_MAX_WIDTH_PX = 100;

export interface AreaReportPreviewMapPoint {
  id: string;
  lat: number;
  lng: number;
  /** 「解析対象となった地点一覧」と対応する1始まりの番号。 */
  number: number;
  segments: DonutSegment[];
}

/** スパイダーファイ後の表示用地点 (真の座標 + 表示座標 + オフセットの有無)。 */
interface PlacedPoint extends AreaReportPreviewMapPoint {
  displayLat: number;
  displayLng: number;
  offset: boolean;
}

export interface AreaReportPreviewMapProps {
  points: AreaReportPreviewMapPoint[];
  /** 選択に使った多角形の頂点 (`StreetSurveyResultsMap` のエリア選択から渡される)。 */
  polygon: { latitude: number; longitude: number }[] | null;
  className?: string;
}

export interface AreaReportPreviewMapHandle {
  /** 現在の地図 (地点＋エリア＋縮尺バー込み) を PNG dataURL としてキャプチャする。 */
  capture: () => Promise<string | null>;
}

/** SVG markup 文字列を画像として読み込む (donut を canvas に drawImage するため)。 */
function loadSvgImage(svgMarkup: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
  });
}

function haversineMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** 距離を 1/2/3/5/10 系列の「きりのいい数値」に丸める (MapLibre/Mapbox GL JS 本家 ScaleControl と同じ式)。 */
function roundNiceDistance(num: number): number {
  const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1);
  const d = num / pow10;
  const step = d >= 10 ? 10 : d >= 5 ? 5 : d >= 3 ? 3 : d >= 2 ? 2 : 1;
  return pow10 * step;
}

interface ScaleBarInfo {
  widthPx: number;
  label: string;
}

/** 現在の地図中心緯度における画面px→実距離の換算から、縮尺バーの幅とラベルを算出する。 */
function computeScaleBar(
  map: MaplibreMap,
  maxWidthPx = SCALE_BAR_MAX_WIDTH_PX,
): ScaleBarInfo | null {
  const container = map.getContainer();
  const y = container.clientHeight / 2;
  const left = map.unproject([0, y]);
  const right = map.unproject([maxWidthPx, y]);
  const maxMeters = haversineMeters(
    { lat: left.lat, lng: left.lng },
    { lat: right.lat, lng: right.lng },
  );
  if (!Number.isFinite(maxMeters) || maxMeters <= 0) return null;
  const niceMeters = roundNiceDistance(maxMeters);
  const widthPx = maxWidthPx * (niceMeters / maxMeters);
  const label =
    niceMeters >= 1000 ? `${Math.round(niceMeters / 100) / 10}km` : `${Math.round(niceMeters)}m`;
  return { widthPx, label };
}

export const AreaReportPreviewMap = forwardRef<
  AreaReportPreviewMapHandle,
  AreaReportPreviewMapProps
>(function AreaReportPreviewMap({ points, polygon, className }, ref) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  // capture() が DOM Marker と同じ (スパイダーファイ後の) 位置を使うための最新値。
  const placedRef = useRef<PlacedPoint[]>([]);
  const [ready, setReady] = useState(false);
  const [scaleBar, setScaleBar] = useState<ScaleBarInfo | null>(null);

  useImperativeHandle(
    ref,
    (): AreaReportPreviewMapHandle => ({
      capture: async () => {
        const map = mapRef.current;
        if (!map) return null;
        try {
          map.triggerRepaint();
          await new Promise<void>((resolve) => {
            map.once('idle', () => resolve());
            setTimeout(() => resolve(), 500);
          });
          const mapCanvas = map.getCanvas();
          if (!mapCanvas || typeof mapCanvas.toDataURL !== 'function') return null;

          const composite = document.createElement('canvas');
          composite.width = mapCanvas.width;
          composite.height = mapCanvas.height;
          const ctx = composite.getContext('2d');
          if (!ctx) return mapCanvas.toDataURL('image/png');
          ctx.drawImage(mapCanvas, 0, 0);

          const cssW = mapCanvas.clientWidth || mapCanvas.width;
          const cssH = mapCanvas.clientHeight || mapCanvas.height;
          const dpr = cssW > 0 ? mapCanvas.width / cssW : 1;
          const size = MARKER_SIZE * dpr;

          for (const p of placedRef.current) {
            try {
              const point = map.project([p.displayLng, p.displayLat]);
              const svg = buildDonutSvgMarkup(
                p.segments,
                size,
                Math.max(4, size * 0.32),
                '#94A3B8',
              );
              const img = await loadSvgImage(svg);
              if (!img) continue;
              const x = point.x * dpr - size / 2;
              const y = point.y * dpr - size / 2;
              ctx.drawImage(img, x, y, size, size);
              ctx.fillStyle = '#0f172a';
              ctx.font = `bold ${size * 0.34}px sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(String(p.number), point.x * dpr, point.y * dpr);
            } catch {
              /* 1点の描画失敗で全体を止めない */
            }
          }

          // 縮尺バー (画面右下、オンスクリーンのオーバーレイと同じ位置・見た目)。
          const bar = computeScaleBar(map);
          if (bar) {
            const marginRight = 10 * dpr;
            const marginBottom = 10 * dpr;
            const barWidthDpr = bar.widthPx * dpr;
            const barH = 4 * dpr;
            const rightX = cssW * dpr - marginRight;
            const leftX = rightX - barWidthDpr;
            const baseY = cssH * dpr - marginBottom;
            ctx.strokeStyle = '#334155';
            ctx.fillStyle = 'rgba(255,255,255,0.75)';
            ctx.lineWidth = Math.max(1, 1.5 * dpr);
            // 背景板 (地図の上でも視認できるように)
            ctx.fillRect(
              leftX - 4 * dpr,
              baseY - barH - 14 * dpr,
              barWidthDpr + 8 * dpr,
              barH + 18 * dpr,
            );
            ctx.beginPath();
            ctx.moveTo(leftX, baseY - barH);
            ctx.lineTo(leftX, baseY);
            ctx.lineTo(rightX, baseY);
            ctx.lineTo(rightX, baseY - barH);
            ctx.stroke();
            ctx.fillStyle = '#334155';
            ctx.font = `${11 * dpr}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText(bar.label, (leftX + rightX) / 2, baseY - barH - 2 * dpr);
          }

          return composite.toDataURL('image/png');
        } catch (e) {
          console.warn('[AreaReportPreviewMap] capture failed', e);
          return null;
        }
      },
    }),
    [],
  );

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          gsi: {
            type: 'raster',
            tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
            tileSize: 256,
            maxzoom: 18,
            attribution: '地理院タイル',
          },
        },
        layers: [{ id: 'gsi', type: 'raster', source: 'gsi' }],
      },
      center: [139.767, 35.681],
      zoom: 12,
      attributionControl: false,
      // capture() で toDataURL() する前提のため WebGL バッファを保持する。
      preserveDrawingBuffer: true,
    });
    map.on('load', () => setReady(true));
    mapRef.current = map;
    const markers = markersRef.current;
    return () => {
      markers.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  // 縮尺バー — オンスクリーン表示用 (ズーム/パンのたび再計算)。capture() 側は自前で
  // computeScaleBar を呼び直すため、この state はオンスクリーンのオーバーレイ表示専用。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const update = () => setScaleBar(computeScaleBar(map));
    update();
    map.on('move', update);
    map.on('resize', update);
    return () => {
      map.off('move', update);
      map.off('resize', update);
    };
  }, [ready]);

  // エリア (多角形) — ネイティブレイヤーのまま。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const polygonFc: GeoJSON.FeatureCollection =
      polygon && polygon.length >= 3
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'Polygon',
                  coordinates: [
                    [
                      ...polygon.map((c) => [c.longitude, c.latitude]),
                      [polygon[0]!.longitude, polygon[0]!.latitude],
                    ],
                  ],
                },
              },
            ],
          }
        : { type: 'FeatureCollection', features: [] };

    if (!map.getSource(POLYGON_SOURCE_ID)) {
      map.addSource(POLYGON_SOURCE_ID, { type: 'geojson', data: polygonFc });
      map.addLayer({
        id: POLYGON_LAYER_ID,
        type: 'fill',
        source: POLYGON_SOURCE_ID,
        paint: { 'fill-color': '#0EA5E9', 'fill-opacity': 0.18, 'fill-outline-color': '#0EA5E9' },
      });
    } else {
      (map.getSource(POLYGON_SOURCE_ID) as maplibregl.GeoJSONSource).setData(polygonFc);
    }
  }, [ready, polygon]);

  // 地点 — 結果地図と同じドーナツ DOM Marker + 中央に番号 + 近接クラスタのスパイダーファイ。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const bounds = new maplibregl.LngLatBounds();
    let hasPoint = false;
    for (const p of points) {
      if (p.lat === 0 && p.lng === 0) continue;
      bounds.extend([p.lng, p.lat]);
      hasPoint = true;
    }
    if (polygon) {
      for (const c of polygon) {
        bounds.extend([c.longitude, c.latitude]);
        hasPoint = true;
      }
    }
    if (hasPoint) {
      map.fitBounds(bounds, { padding: 40, maxZoom: 18, duration: 0 });
    }

    // fitBounds (duration:0) の反映を1tick待ってから、確定した画面座標でスパイダーファイする。
    const t = window.setTimeout(() => {
      try {
        map.resize();
      } catch {
        /* ignore */
      }

      markersRef.current.forEach((m) => m.remove());
      const placed = declutterPoints(map, points);
      placedRef.current = placed;

      markersRef.current = placed.map((p) => {
        const padSize = MARKER_SIZE + 6;
        const el = document.createElement('div');
        el.style.cssText = `width:${padSize}px;height:${padSize}px;border-radius:50%;background:white;border:1.5px solid rgba(15,23,42,0.2);box-shadow:0 1px 4px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;`;
        const donutWrap = document.createElement('div');
        donutWrap.style.cssText = `width:${MARKER_SIZE}px;height:${MARKER_SIZE}px;`;
        donutWrap.innerHTML = buildDonutSvgMarkup(
          p.segments,
          MARKER_SIZE,
          Math.max(4, MARKER_SIZE * 0.32),
          '#94A3B8',
        );
        el.appendChild(donutWrap);
        const label = document.createElement('span');
        label.textContent = String(p.number);
        label.style.cssText = `position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:${MARKER_SIZE * 0.34}px;font-weight:700;color:#0f172a;line-height:1;`;
        el.appendChild(label);
        return new maplibregl.Marker({ element: el })
          .setLngLat([p.displayLng, p.displayLat])
          .addTo(map);
      });

      // 吹き出し線: オフセットした地点だけ、真の位置 → 表示位置を細線で結ぶ。
      const leaderFc: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: placed
          .filter((p) => p.offset)
          .map((p) => ({
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [
                [p.lng, p.lat],
                [p.displayLng, p.displayLat],
              ],
            },
          })),
      };
      if (!map.getSource(LEADER_SOURCE_ID)) {
        map.addSource(LEADER_SOURCE_ID, { type: 'geojson', data: leaderFc });
        map.addLayer({
          id: LEADER_LAYER_ID,
          type: 'line',
          source: LEADER_SOURCE_ID,
          paint: { 'line-color': '#64748b', 'line-width': 1.5, 'line-dasharray': [2, 1.5] },
        });
      } else {
        (map.getSource(LEADER_SOURCE_ID) as maplibregl.GeoJSONSource).setData(leaderFc);
      }
    }, 80);

    return () => window.clearTimeout(t);
  }, [ready, points, polygon]);

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className={className ?? 'w-full h-64 rounded border border-slate-200'}
        data-testid="area-report-preview-map"
      />
      {scaleBar && (
        <div className="absolute bottom-2 right-2 bg-white/75 rounded px-1.5 pt-0.5 pb-1 pointer-events-none">
          <div className="text-[10px] text-slate-700 text-center leading-none mb-0.5">
            {scaleBar.label}
          </div>
          <div
            className="border-b-2 border-l-2 border-r-2 border-slate-600 h-1"
            style={{ width: `${scaleBar.widthPx}px` }}
          />
        </div>
      )}
    </div>
  );
});

/**
 * 画面座標が近い地点をクラスタ化し、2点以上のクラスタは中心から円状に均等配置した
 * 仮想位置 (displayLat/displayLng) を割り当てる (スパイダーファイ)。1点だけのクラスタは
 * 真の位置のまま (`offset:false`)。
 */
function declutterPoints(map: MaplibreMap, points: AreaReportPreviewMapPoint[]): PlacedPoint[] {
  const valid = points.filter((p) => !(p.lat === 0 && p.lng === 0));
  const projected = valid.map((p) => ({ p, screen: map.project([p.lng, p.lat]) }));

  const groups: { items: typeof projected }[] = [];
  for (const item of projected) {
    const group = groups.find((g) => {
      const cx = g.items.reduce((s, i) => s + i.screen.x, 0) / g.items.length;
      const cy = g.items.reduce((s, i) => s + i.screen.y, 0) / g.items.length;
      return Math.hypot(item.screen.x - cx, item.screen.y - cy) < CLUSTER_THRESHOLD_PX;
    });
    if (group) group.items.push(item);
    else groups.push({ items: [item] });
  }

  const result: PlacedPoint[] = [];
  for (const g of groups) {
    if (g.items.length === 1) {
      const { p } = g.items[0]!;
      result.push({ ...p, displayLat: p.lat, displayLng: p.lng, offset: false });
      continue;
    }
    const n = g.items.length;
    const cx = g.items.reduce((s, i) => s + i.screen.x, 0) / n;
    const cy = g.items.reduce((s, i) => s + i.screen.y, 0) / n;
    // 隣接する地点同士が重ならない半径 (円周上に n 点を等間隔配置する弦長の式から逆算)。
    const radius = Math.max(MARKER_SIZE, CLUSTER_THRESHOLD_PX / (2 * Math.sin(Math.PI / n)) + 4);
    g.items.forEach((item, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      const ox = cx + radius * Math.cos(angle);
      const oy = cy + radius * Math.sin(angle);
      const lngLat = map.unproject([ox, oy]);
      result.push({ ...item.p, displayLat: lngLat.lat, displayLng: lngLat.lng, offset: true });
    });
  }
  return result;
}
